import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, User, Video, MapPin } from "lucide-react";

const Sessions = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    sessionType: "single",
    delivery: "online",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sessions/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: "", email: "", sessionType: "single", delivery: "online", message: "" });
      }
    } catch (error) {
      console.error("Error submitting inquiry:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const packages = [
    {
      id: "single",
      name: "Single Session",
      subtitle: "Emotional Reset",
      price: 150,
      sessions: 1,
      duration: "90 minutes",
      description: "A powerful entry point for emotional awareness and nervous system regulation.",
      includes: [
        "Intake conversation (15 min)",
        "Guided breathwork (45 min)",
        "Integration & next steps (30 min)"
      ],
      outcome: "Emotional lightness, grounded clarity, tangible relief."
    },
    {
      id: "three-pack",
      name: "Deep Reset",
      subtitle: "3-Session Package",
      price: 400,
      sessions: 3,
      duration: "3 x 90 minutes",
      description: "Build consistent regulation and lasting self-awareness over 3-4 weeks.",
      includes: [
        "Session 1: Nervous system regulation + safety",
        "Session 2: Pattern awareness + emotional release",
        "Session 3: Integration + personal daily ritual",
        "Personalized breathwork plan"
      ],
      outcome: "Clarity, calm, and a repeatable emotional grounding practice.",
      popular: true
    },
    {
      id: "six-pack",
      name: "Transformation",
      subtitle: "6-Session Journey",
      price: 750,
      sessions: 6,
      duration: "6 x 90 minutes",
      description: "Repattern your emotional landscape. Release chronic holding. Rewire habits.",
      includes: [
        "6 x 90-minute sessions",
        "Personalized breath sequence",
        "Custom guided meditation recording",
        "Between-session support"
      ],
      outcome: "Emotional mastery, consistency, and embodied awareness."
    }
  ];

  return (
    <div className="min-h-screen bg-ink" data-testid="sessions-page">
      {/* Hero */}
      <section className="pt-32 pb-20 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">
            Private Work
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#EAEAEA] mb-6">
            1:1 Recalibration
          </h1>
          <p className="text-[#A1A1AA] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Emotional recalibration for adults in transition who want grounded strength, not therapy.
          </p>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-2xl text-[#EAEAEA] mb-6">This is for you if...</h2>
              <ul className="space-y-3 text-[#A1A1AA]">
                {[
                  "You feel stuck, but you're not broken",
                  "Something's off and you can't name it",
                  "You're tired but wired",
                  "You're navigating a transition — relationship, career, identity",
                  "You want tools, not endless talk",
                  "You're ready to feel grounded again"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={18} className="text-gold mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-2xl text-[#EAEAEA] mb-6">This is not for you if...</h2>
              <ul className="space-y-3 text-[#A1A1AA]">
                {[
                  "You're in active crisis or addiction",
                  "You're looking for someone to fix you",
                  "You want spiritual bypassing or toxic positivity",
                  "You need clinical therapy (I can refer you)",
                  "You're not ready to take responsibility"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[#666] mt-1 flex-shrink-0">—</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Experience */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4 text-center">What happens in a session</h2>
          <p className="text-[#A1A1AA] text-center mb-12 max-w-2xl mx-auto">
            Each 90-minute session combines breathwork and coaching to create lasting change in your nervous system.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Intake",
                duration: "15 min",
                description: "We map your current state — triggers, patterns, what's actually going on in your body."
              },
              {
                title: "Breathwork",
                duration: "45 min",
                description: "Guided somatic practice to reset your nervous system and release stored tension."
              },
              {
                title: "Integration",
                duration: "30 min",
                description: "Processing, journaling, and concrete next steps you can use immediately."
              }
            ].map((phase, i) => (
              <div key={i} className="p-6 border border-white/5 rounded-sm">
                <div className="text-gold text-xs uppercase tracking-widest mb-2">{phase.duration}</div>
                <h3 className="font-heading text-xl text-[#EAEAEA] mb-3">{phase.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20 bg-paper">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4 text-center">Choose your path</h2>
          <p className="text-[#A1A1AA] text-center mb-12">
            Start with a single session, or commit to deeper transformation.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`p-8 border rounded-sm relative ${
                  pkg.popular 
                    ? "border-gold/50 bg-gold/5" 
                    : "border-white/5"
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-ink text-xs uppercase tracking-widest px-3 py-1 rounded-sm">
                    Most Popular
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="font-heading text-2xl text-[#EAEAEA]">{pkg.name}</h3>
                  <p className="text-gold text-sm">{pkg.subtitle}</p>
                </div>
                
                <div className="mb-6">
                  <span className="font-heading text-4xl text-[#EAEAEA]">${pkg.price}</span>
                  <span className="text-[#A1A1AA] text-sm ml-2">AUD</span>
                </div>
                
                <p className="text-[#A1A1AA] text-sm mb-6">{pkg.description}</p>
                
                <div className="mb-6">
                  <p className="text-[#666] text-xs uppercase tracking-widest mb-3">Includes</p>
                  <ul className="space-y-2">
                    {pkg.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#A1A1AA] text-sm">
                        <Check size={14} className="text-gold mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="pt-4 border-t border-white/5">
                  <p className="text-[#666] text-xs uppercase tracking-widest mb-2">Outcome</p>
                  <p className="text-[#EAEAEA] text-sm italic">{pkg.outcome}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Options */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl text-[#EAEAEA] mb-8">How we work together</h2>
          <div className="flex flex-col md:flex-row gap-8 justify-center">
            <div className="flex items-center gap-3 text-[#A1A1AA]">
              <Video size={24} className="text-gold" />
              <span>Online via Zoom — anywhere in the world</span>
            </div>
            <div className="flex items-center gap-3 text-[#A1A1AA]">
              <MapPin size={24} className="text-gold" />
              <span>In-person — Townsville, Australia</span>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-20 bg-paper">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4 text-center">Begin the conversation</h2>
          <p className="text-[#A1A1AA] text-center mb-10">
            Tell me what's going on. I'll respond within 48 hours.
          </p>
          
          {submitted ? (
            <div className="text-center p-8 border border-gold/30 rounded-sm bg-gold/5">
              <Check size={48} className="text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">Inquiry received</h3>
              <p className="text-[#A1A1AA]">I'll be in touch within 48 hours to discuss next steps.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="session-inquiry-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Your name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-4 py-3 text-[#EAEAEA] focus:border-gold/50 focus:outline-none transition-colors"
                    data-testid="session-name-input"
                  />
                </div>
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-4 py-3 text-[#EAEAEA] focus:border-gold/50 focus:outline-none transition-colors"
                    data-testid="session-email-input"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">I'm interested in</label>
                  <select
                    value={formData.sessionType}
                    onChange={(e) => setFormData({...formData, sessionType: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-4 py-3 text-[#EAEAEA] focus:border-gold/50 focus:outline-none transition-colors"
                    data-testid="session-type-select"
                  >
                    <option value="single">Single Session ($150)</option>
                    <option value="three-pack">3-Session Package ($400)</option>
                    <option value="six-pack">6-Session Journey ($750)</option>
                    <option value="unsure">Not sure yet</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">Preferred delivery</label>
                  <select
                    value={formData.delivery}
                    onChange={(e) => setFormData({...formData, delivery: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-4 py-3 text-[#EAEAEA] focus:border-gold/50 focus:outline-none transition-colors"
                    data-testid="session-delivery-select"
                  >
                    <option value="online">Online (Zoom)</option>
                    <option value="in-person">In-person (Townsville)</option>
                    <option value="either">Either works</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">What's going on for you right now?</label>
                <textarea
                  rows={5}
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell me what you're experiencing, what you're looking for, or any questions you have..."
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-4 py-3 text-[#EAEAEA] focus:border-gold/50 focus:outline-none transition-colors placeholder:text-[#666]"
                  data-testid="session-message-textarea"
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gold text-ink font-body uppercase tracking-widest py-4 rounded-sm hover:bg-gold/90 transition-colors disabled:opacity-50"
                data-testid="session-submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Inquiry"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Back to Library */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[#A1A1AA] mb-4">Prefer to start with the books?</p>
          <Link 
            to="/library" 
            className="inline-flex items-center gap-2 text-gold hover:text-gold/80 transition-colors group"
          >
            <span className="text-sm uppercase tracking-widest">Explore the Library</span>
            <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Sessions;
